import { Box, Divider, Typography } from '@mui/material'
import { GetStaticProps } from 'next'
import { sortBy } from 'ramda'

import { CaseStudy } from '@ecm/data/CaseStudy'
import { runEff } from '@ecm/effect'
import CaseStudyCard from '@ecm/ui/component/caseStudies/CaseStudyCard'
import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import DefaultLayout from '@ecm/ui/layout/Default'

import heroIllustration from '/public/images/illustration/case-studies-hero-image.png'

type CaseSudiesProps = {
  caseStudies: CaseStudy[]
}

const CaseStudies = ({ caseStudies }: CaseSudiesProps) => (
  <DefaultLayout
    hideFinalCta
    seoTitle="Case studies | Earth Class Mail"
    seoDescription="Explore Earth Class Mail customer success stories and case studies to see how we help them refocus on their business operations and growth."
  >
    <Box
      sx={{
        background: `linear-gradient(180deg, ${Color.smoke.normal} 700px, rgba(255, 255, 255, 0) 1000px)`,
      }}
    >
      <MainContainer>
        <Box
          display="grid"
          gridTemplateColumns={{ sm: '1fr 1fr', xs: '1fr' }}
          gap="32px"
          alignItems="center"
          py="64px"
        >
          <Typography variant="h1" mb="16px">
            Case Studies
          </Typography>

          <Box>
            <CloudflareImage src={heroIllustration} />
          </Box>
        </Box>
        <Box
          display="grid"
          gridTemplateColumns={{ lg: '1fr 1fr 1fr', md: '1fr 1fr', xs: '1fr' }}
          gap={{ lg: '60px', md: '30px', xs: '16px' }}
          pb="80px"
        >
          {caseStudies.map(({ organizationName, logoImg, description, industry, slug }, i) => (
            <CaseStudyCard
              index={i}
              key={organizationName}
              organizationName={organizationName}
              logoImg={logoImg}
              description={description}
              industry={industry}
              slug={slug}
            />
          ))}
        </Box>
      </MainContainer>
    </Box>
    <Divider sx={{ backgroundColor: Color.smoke.normal }} />
  </DefaultLayout>
)

export default CaseStudies

export const getStaticProps: GetStaticProps = async () => {
  const caseStudies: CaseStudy[] = await runEff(eff => {
    try {
      return eff.getCaseStudies()
    } catch {
      return []
    }
  })

  return { props: { caseStudies: sortBy(c => c.organizationName, caseStudies) } }
}
